import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { modalOverlayProps } from '../utils/modalOverlayProps';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './AlpacaAssets.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AlpacaAssets = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [filterTradable, setFilterTradable] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFractionable, setFilterFractionable] = useState(null);
  const [chartModalAsset, setChartModalAsset] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);

  const { token, logout } = useAuth();

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      if (filterTradable !== null) {
        params.append('tradable', filterTradable.toString());
      }

      if (filterStatus) {
        params.append('status', filterStatus);
      }

      if (filterFractionable !== null) {
        params.append('fractionable', filterFractionable.toString());
      }

      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`/api/alpaca/assets?${params.toString()}`, { headers });

      if (response.status === 401) {
        let msg = 'Token invalide ou expiré';
        try {
          const j = await response.json();
          msg = j.message || msg;
        } catch {}
        setError(msg);
        await logout();
        return;
      }

      if (!response.ok) {
        let msg = 'Erreur lors du chargement des actions';
        try {
          const j = await response.json();
          msg = j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      if (data.success) {
        setAssets(data.assets || []);
        setPagination(data.pagination || pagination);
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, [filterTradable, filterStatus, filterFractionable]);

  const handleSearch = () => {
    loadData(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    loadData(newPage);
  };

  const loadChartDataForModal = async (symbolToLoad) => {
    if (!symbolToLoad || !token) return;
    setChartLoading(true);
    setChartError(null);
    setChartData(null);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      const symbolUpper = symbolToLoad.toUpperCase().trim();
      const url = `/api/alpaca/history/${encodeURIComponent(symbolUpper)}?start_date=${startDateStr}&end_date=${endDateStr}`;
      const headers = { 'Authorization': `Bearer ${token}` };
      const response = await fetch(url, { headers });
      if (response.status === 401) {
        logout();
        return;
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Erreur lors du chargement');
      }
      if (!data.data || data.data.length === 0) {
        setChartError(`Aucune donnée historique pour ${symbolUpper}. Mettez à jour l'historique depuis la page "Vérification historique".`);
        return;
      }
      const sortedData = [...data.data].sort((a, b) =>
        new Date(a.trade_date) - new Date(b.trade_date)
      );
      const labels = sortedData.map(item => {
        const date = new Date(item.trade_date);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      });
      const closePrices = sortedData.map(item => parseFloat(item.close || 0));
      setChartData({
        labels,
        datasets: [{
          label: 'Prix de clôture ($)',
          data: closePrices,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
        }]
      });
    } catch (err) {
      setChartError(err.message || 'Erreur lors du chargement du graphique');
    } finally {
      setChartLoading(false);
    }
  };

  const openChartModal = (asset) => {
    setChartModalAsset(asset);
    setChartData(null);
    setChartError(null);
  };

  const closeChartModal = () => {
    setChartModalAsset(null);
    setChartData(null);
    setChartError(null);
  };

  useEffect(() => {
    if (!chartModalAsset?.symbol) return;
    loadChartDataForModal(chartModalAsset.symbol);
  }, [chartModalAsset?.symbol]);

  const formatBoolean = (value) => {
    return value ? '✅' : '❌';
  };

  const chartModalOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: chartModalAsset ? `Historique - ${chartModalAsset.symbol} (90 jours)` : '',
        font: { size: 16, weight: 'bold' }
      },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: {
        type: 'linear',
        title: { display: true, text: 'Prix ($)' }
      }
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false }
  };

  return (
    <div className="assets-page">
      <div className="assets-header">
        <h1 className="assets-title">📈 Actions</h1>
        <p className="assets-subtitle">Liste des actions disponibles sur Alpaca</p>
      </div>

      {/* Filtres */}
      <div className="assets-filters">
        <div className="filter-group">
          <input
            type="text"
            className="filter-input"
            placeholder="Rechercher (symbole ou nom)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button className="filter-button" onClick={handleSearch}>
            🔍 Rechercher
          </button>
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filterTradable === null ? '' : filterTradable.toString()}
            onChange={(e) => {
              const value = e.target.value === '' ? null : e.target.value === 'true';
              setFilterTradable(value);
              setTimeout(() => loadData(1), 100);
            }}
          >
            <option value="">Tous (négociable)</option>
            <option value="true">Négociable uniquement</option>
            <option value="false">Non négociable uniquement</option>
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setTimeout(() => loadData(1), 100);
            }}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>

          <select
            className="filter-select"
            value={filterFractionable === null ? '' : filterFractionable.toString()}
            onChange={(e) => {
              const value = e.target.value === '' ? null : e.target.value === 'true';
              setFilterFractionable(value);
              setTimeout(() => loadData(1), 100);
            }}
          >
            <option value="">Tous (fractionnable)</option>
            <option value="true">Fractionnable uniquement</option>
            <option value="false">Non fractionnable uniquement</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des actions...</p>
        </div>
      ) : (
        <>
          <div className="assets-stats">
            <p>Total : <strong>{pagination.total}</strong> actions</p>
            <p>Page {pagination.page} sur {pagination.pages}</p>
          </div>

          {assets.length === 0 ? (
            <div className="empty-message">
              <p>Aucune action trouvée.</p>
            </div>
          ) : (
            <>
              <div className="assets-table-container">
                <table className="assets-table">
                  <thead>
                    <tr>
                      <th>Symbole</th>
                      <th>Nom</th>
                      <th>Bourse</th>
                      <th>Statut</th>
                      <th>Classe</th>
                      <th>Négociable</th>
                      <th>Sur marge</th>
                      <th>À découvert</th>
                      <th>Empruntable</th>
                      <th>Fractionnable</th>
                      <th className="center">Graphique</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset) => (
                      <tr key={asset.symbol || asset.id}>
                        <td><strong>{asset.symbol}</strong></td>
                        <td>{asset.name || '-'}</td>
                        <td>{asset.market_name || asset.exchange || '-'}</td>
                        <td>
                          <span className={`status-badge status-${asset.status || 'unknown'}`}>
                            {asset.status || 'N/A'}
                          </span>
                        </td>
                        <td>{asset.class || '-'}</td>
                        <td className="center">{formatBoolean(asset.tradable)}</td>
                        <td className="center">{formatBoolean(asset.marginable)}</td>
                        <td className="center">{formatBoolean(asset.shortable)}</td>
                        <td className="center">{formatBoolean(asset.easy_to_borrow)}</td>
                        <td className="center">{formatBoolean(asset.fractionable)}</td>
                        <td className="center">
                          <button
                            type="button"
                            className="asset-chart-link"
                            onClick={() => openChartModal({ symbol: asset.symbol, name: asset.name })}
                            title="Voir l'historique"
                          >
                            📊
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    ← Précédent
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} / {pagination.pages}
                  </span>
                  <button
                    className="pagination-button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Popup graphique historique */}
      {chartModalAsset && (
        <div className="assets-chart-modal-overlay" {...modalOverlayProps(closeChartModal)}>
          <div className="assets-chart-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="assets-chart-modal-header">
              <h2>Historique – {chartModalAsset.symbol}{chartModalAsset.name ? ` (${chartModalAsset.name})` : ''}</h2>
              <button type="button" className="assets-chart-modal-close" onClick={closeChartModal} aria-label="Fermer">
                ×
              </button>
            </div>
            <div className="assets-chart-modal-body">
              {chartLoading && (
                <div className="assets-chart-modal-loading">
                  <div className="loading-spinner"></div>
                  <p>Chargement du graphique...</p>
                </div>
              )}
              {chartError && !chartLoading && (
                <div className="error-message">{chartError}</div>
              )}
              {chartData && !chartLoading && (
                <div className="assets-chart-modal-chart">
                  <Line data={chartData} options={chartModalOptions} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlpacaAssets;
